import { zodResolver } from '@hookform/resolvers/zod';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { useQueryClient } from '@tanstack/react-query';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { FormProvider, useForm } from 'react-hook-form';

import { useGetCourseCompletionCriteria } from 'api/hooks/private/courses/GetCourseCompletionCriteria.ts';
import { useUpdateCourseCompletionCriteria } from 'api/hooks/private/courses/UpdateCourseCompletionCriteria.ts';

import IntervalInput from 'components/FormInputs/IntervalInput';

import { getApiErrorMessage } from 'utils/getApiErrorMessage.ts';
import normalizePgInterval from 'utils/normalizePgInterval.ts';

import DateModified from '../../components/DateModified.tsx';
import DialogFormSection from '../../components/DialogFormSection.tsx';
import FeedbackDialog from '../../FeedbackDialog/index.ts';
import { FormInputValues, FormOutputValues, FormSchema } from './formSchema.ts';

dayjs.extend(calendar);

interface UpdateCourseCompletionCriteriaDialogPayload {
  /**
   * The course id to update
   */
  courseId: string;
}

export function UpdateCourseCompletionCriteriaDialog({
  payload,
  open,
  onClose,
}: DialogProps<UpdateCourseCompletionCriteriaDialogPayload>) {
  const queryClient = useQueryClient();
  const dialogs = useDialogs();

  const updateCourseCompletionCriteria = useUpdateCourseCompletionCriteria();

  const courseCompletionCriteria = useGetCourseCompletionCriteria({
    variables: {
      courseId: payload.courseId,
    },
    enabled: !!payload.courseId,
  });

  const form = useForm<FormInputValues, unknown, FormOutputValues>({
    mode: 'all',
    resolver: zodResolver(FormSchema),
    values: {
      performance_time_required: normalizePgInterval(courseCompletionCriteria.data?.performance_time_required),
      observation_time_required: normalizePgInterval(courseCompletionCriteria.data?.observation_time_required),
    },
  });

  const { handleSubmit } = form;

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onSubmit = async (data: FormOutputValues) => {
    try {
      // show confirmation dialog
      const confirmed = await dialogs.confirm('This will affect all students enrolled in the course.', {
        okText: 'Update',
        cancelText: 'Cancel',
      });

      // if user cancels, return
      if (!confirmed) {
        return;
      }

      const result = await updateCourseCompletionCriteria.mutateAsync({
        courseId: payload.courseId,
        policy: data,
      });

      if (result) {
        // close the dialog
        handleClose();

        // invalidate the query key
        queryClient.invalidateQueries({
          queryKey: ['course_policy', { id: payload.courseId }],
        });

        // show success message
        dialogs.open(FeedbackDialog, {
          icon: 'success',
          title: 'Requirements Updated',
        });
      }
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error ',
        message: getApiErrorMessage(error),
      });
    }
  };

  return (
    <Dialog fullWidth open={open} onClose={handleClose}>
      <DialogTitle>Update Course Completion Requirements</DialogTitle>

      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <DialogContent>
            <DialogFormSection title="Performance Time Required">
              <Grid size={12}>
                <IntervalInput name="performance_time_required" label="Performance Time Required" hours minutes />
              </Grid>
            </DialogFormSection>

            <DialogFormSection title="Observation Time Required">
              <Grid size={12}>
                <IntervalInput name="observation_time_required" hours minutes />
              </Grid>
            </DialogFormSection>

            <Box pt={2}>
              <DateModified
                prefix="Updated: "
                date={courseCompletionCriteria.data?.updated_at}
                user={courseCompletionCriteria.data?.updated_by_user}
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit">Update</Button>
          </DialogActions>
        </FormProvider>
      </form>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={updateCourseCompletionCriteria.isPending}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default UpdateCourseCompletionCriteriaDialog;
